/** Builds an RFC 4122 version 4 UUID from `crypto.getRandomValues`, which every context has. */
function uuidFromRandomValues(): string {
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * A random RFC 4122 version 4 UUID. Browsers expose `crypto.randomUUID` only in secure contexts
 * (https and localhost), so on a plain http origin it is undefined and calling it throws. There
 * this builds the same format from `crypto.getRandomValues`.
 */
export function randomUuid(): string {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  return uuidFromRandomValues();
}

/**
 * Gives `crypto` a `randomUUID` where the browser left it out, which it does on every non-secure
 * origin (plain http other than localhost). Our own code calls `randomUuid()`, but third-party
 * code such as the OpenUI chat store and charts calls `crypto.randomUUID()` directly and would
 * throw there. A native implementation is never replaced, and the added property is
 * non-enumerable.
 */
export function installRandomUuidPolyfill(): void {
  const cryptoApi = globalThis.crypto;
  if (!cryptoApi || typeof cryptoApi.randomUUID === 'function') return;
  Object.defineProperty(cryptoApi, 'randomUUID', {
    value: uuidFromRandomValues,
    enumerable: false,
    configurable: true,
    writable: true,
  });
}
